import { err, ok } from '@src/services/common/Result';
import { normalizeForCompare } from '@src/utility/strings';

import { keptDurationSec, overlapsAnySpan } from './clipSpanOverlap';
import { dropSuperseded } from './DubPlacementService';

import type { ClipSpan } from './DubPlacementService';
// Type-only, and deliberately not moved into the editor — the reasoning is
// written out at the same import in `DubPlacementService`.
import type { DubManifestDTO } from '@src/repositories/DubbingRepository';
import type { Result } from '@src/services/common/Result';
import type { TranscriptElement } from '@src/types.ts/transcript';
import type {
  ParentPhrase,
  TranslationPhrase,
} from '@src/types.ts/translation';

/**
 * How much of ONE clip's translated speech actually has a dubbed voice.
 *
 * Pure: no MobX, no React, no IO. Handed a clip's kept spans, the saved
 * translation, the transcript and the dub manifest, it answers three questions —
 * how many of this clip's lines are dubbed, which are missing, and which are
 * dubbed from words that have since changed.
 *
 * **Why this cannot be read off the manifest alone.** The manifest lists only
 * lines that WERE dubbed. Coverage is about the ones that were not, so the
 * denominator has to come from the translation. And a translation phrase carries
 * no time, so locating it needs the transcript.
 *
 * The rules that real data forces, rather than the types:
 *
 * - **Read `media_ts` / `media_end_ts`, never `ts`** — on real elements `ts` is
 *   `null`, including on elements that are plainly present.
 * - **Punctuation carries no timestamp**, so a reference that resolves to
 *   nothing is tolerated, never fatal.
 * - **Count against the clip's own lines, never the story's**, or a clip is
 *   reported as covered on the strength of audio it does not contain.
 * - **Count a line once**, however many kept spans it touches — unlike
 *   placement, which deliberately emits one piece per span so a line cut in two
 *   still sounds on both sides of the cut.
 * - **Superseded segments do not count.** Regenerating a dub currently leaves
 *   the old and the new line in the manifest, so a stale duplicate would inflate
 *   the covered count.
 */

/** One line of the clip that has no dubbed voice. */
export interface DubCoverageGap {
  /**
   * The line's id, which is also the dub's `segmentId`, so a control beside this
   * gap names a line the dubbing run recognises. On a span replacement that is
   * the host sentence's id, never the `o_` record's own (see `lineIdOf`).
   */
  phraseId: string;
  /** Where the line starts in the FULL SOURCE video, in seconds. */
  startSec: number;
  /** Where it ends in the full source video, in seconds. */
  endSec: number;
  /** How much of it this clip actually keeps, in seconds, across all spans. */
  keptSec: number;
  /** The translated words, for showing the creator what is missing. */
  text: string;
}

/** One line whose dub was voiced from words that have since changed. */
export interface DubCoverageStale {
  phraseId: string;
  startSec: number;
  endSec: number;
  /** The words the audio was voiced from. */
  dubbedFrom: string;
  /** The words the translation carries now. */
  translationNow: string;
}

export interface DubCoverage {
  /** Lines of the translation that appear anywhere in this clip. */
  inClip: number;
  /**
   * The ids of those lines, dubbed or not, earliest first — the dub's
   * `segmentId`s, as `gaps[].phraseId` is. What a clip-sized re-dub names.
   */
  lineIds: string[];
  /** Of those, lines that have a live (non-superseded) dub. */
  dubbed: number;
  /** Lines in the clip with no dub, earliest first. */
  gaps: DubCoverageGap[];
  /** Lines in the clip whose dub no longer matches the translation, earliest first. */
  stale: DubCoverageStale[];
  /** Seconds of this clip's kept picture that have no dubbed voice over them. */
  gapSeconds: number;
  /**
   * Seconds of this clip's kept picture whose dub was voiced from words that
   * have since changed. Kept picture only, like `gapSeconds`: the parts of a
   * line the clip cut out are not the creator's problem.
   */
  staleSeconds: number;
  /**
   * Phrases no transcript element could place. Reported rather than dropped:
   * silently discarding one would overstate coverage.
   */
  unlocatable: string[];
}

export interface DubCoverageInput {
  /** The clip's kept spans. */
  spans: ClipSpan[];
  /** The saved translation's parent phrases for the language being dubbed. */
  phrases: ParentPhrase[];
  /**
   * The saved translation's fragments for the same language — `phrases[]` on the
   * stored translation, not `parentPhrases[]`.
   *
   * Required, because a line's current text is its live fragments joined. The
   * parent's own `translatedValue` is the text from before the line was split
   * and no ordinary edit ever writes it, so comparing against the parent made
   * the out-of-date warning fire in exactly the wrong situations.
   */
  fragments: TranslationPhrase[];
  /** The transcript elements the phrases point at. */
  elements: TranscriptElement[];
  /** The dub manifest for the same language, or `null` when nothing is dubbed. */
  manifest: DubManifestDTO | null;
}

/**
 * What the coverage rules read from a transcript element: its keys, its kind
 * and its time in the full source video. The store's transcript elements and
 * the editor's own transcript tokens (PLAT-11905, dub-timed captions) both have
 * these.
 */
type CoverageElement = {
  id?: string | number | null;
  source_uuid?: string | null;
  type?: string;
  media_ts?: number | null;
};

/** Where one phrase sits in the full source video. */
interface PhraseTime {
  startSec: number;
  endSec: number;
}

/**
 * Index the transcript by every key a phrase might name.
 *
 * Both `id` and `source_uuid` are indexed because the translation join is
 * `id ?? source_uuid` and real data uses both: a word's `id` can be a plain
 * number while its `source_uuid` is the UUID the translation stored.
 */
function indexElements<E extends CoverageElement>(
  elements: readonly E[],
): Map<string, E[]> {
  const byKey = new Map<string, E[]>();
  for (const element of elements) {
    for (const key of [element.id, element.source_uuid]) {
      if (key === undefined || key === null) continue;
      const bucket = byKey.get(String(key));
      if (bucket) bucket.push(element);
      else byKey.set(String(key), [element]);
    }
  }
  return byKey;
}

/** A timed word's start and its position in the transcript. */
interface WordPlace {
  sec: number;
  index: number;
}

/**
 * Every element's position in the transcript, and for each untimed element the
 * timed words just before and after it — the backend's rule for placing
 * punctuation, which carries no time of its own.
 */
function indexTranscriptPlaces<E extends CoverageElement>(
  elements: readonly E[],
): TranscriptPlaces {
  const indexOf = new Map<CoverageElement, number>();
  const neighbours = new Map<
    CoverageElement,
    { before: WordPlace | null; after: WordPlace | null }
  >();
  const untimed: CoverageElement[] = [];
  let before: WordPlace | null = null;
  elements.forEach((element, index) => {
    indexOf.set(element, index);
    const start = element.type === 'text' ? element.media_ts : null;
    if (start === undefined || start === null) {
      neighbours.set(element, { before, after: null });
      untimed.push(element);
      return;
    }
    before = { sec: start, index };
    for (const waiting of untimed.splice(0)) {
      const entry = neighbours.get(waiting);
      if (entry) entry.after = before;
    }
  });
  return { indexOf, neighbours };
}

interface TranscriptPlaces {
  indexOf: Map<CoverageElement, number>;
  neighbours: Map<
    CoverageElement,
    { before: WordPlace | null; after: WordPlace | null }
  >;
}

/** A fragment holding no letter, digit or combining mark — the backend's test. */
const isPunctuationOnly = (text: string): boolean =>
  !/[\p{L}\p{N}\p{M}]/u.test(text);

/** Opening punctuation (`«`, `(`, `¿`) joins the next word, as the backend splits it. */
const joinsNextWord = (text: string): boolean =>
  text.length > 0 && Array.from(text).every(c => /^[\p{Ps}\p{Pi}¿¡]$/u.test(c));

/** A dash (`—`, `–`) stands between words with a space on each side. */
const standsAlone = (text: string): boolean =>
  text.length > 0 && Array.from(text).every(c => /^\p{Pd}$/u.test(c));

/** Ascending order that treats two equal values, `Infinity` included, as a tie. */
const compareAscending = (a: number, b: number): number =>
  a === b ? 0 : a - b;

/**
 * Where a phrase sits in the source video, or `null` when nothing it names can be
 * placed. Takes the earliest start and the latest end of every element that has a
 * time, so one unresolvable or untimed reference cannot lose the line.
 */
function timeOfPhrase(
  phrase: ParentPhrase,
  byKey: Map<string, TranscriptElement[]>,
): PhraseTime | null {
  let startSec: number | null = null;
  let endSec: number | null = null;

  for (const reference of phrase.originalElementIds ?? []) {
    const candidates = byKey.get(String(reference));
    if (!candidates) continue;
    for (const element of candidates) {
      // Only a word carries a time; punctuation never does, which is why a
      // reference to it is skipped rather than treated as a failure.
      if (element.type !== 'text') continue;
      const start = element.media_ts;
      if (start === undefined || start === null) continue;
      const end = element.media_end_ts ?? start;
      startSec = startSec === null ? start : Math.min(startSec, start);
      endSec = endSec === null ? end : Math.max(endSec, end);
    }
  }

  if (startSec === null || endSec === null) return null;
  return { startSec, endSec };
}

const keysOf = (p: { originalElementIds?: string[] }): string[] =>
  p.originalElementIds ?? [];

/**
 * A span replacement's id starts `o_` — on the fragment that carries the new
 * words and on the extra line record the plugin writes beside it.
 */
const isOverrideId = (id: string | undefined): boolean =>
  (id ?? '').startsWith('o_');

/**
 * The sentence anchor a fragment's words belong to, mirroring the backend's own
 * rule: one recording is filed under this id, so it is also the id the manifest
 * carries.
 */
const anchorOf = (p: TranslationPhrase): string =>
  p.parentId ?? `p_${keysOf(p)[0]}`;

/**
 * Every element id a base fragment owns, mapped to its sentence anchor. Built
 * from ALL base fragments including superseded ones, so a span replacement can
 * still find its home sentence after the words it covers have been dropped.
 */
function indexSentenceByElement(
  fragments: TranslationPhrase[],
): Map<string, string> {
  const sentenceByKey = new Map<string, string>();
  for (const fragment of fragments) {
    if (isOverrideId(fragment.parentId)) continue;
    const anchor = anchorOf(fragment);
    for (const key of keysOf(fragment))
      if (!sentenceByKey.has(key)) sentenceByKey.set(key, anchor);
  }
  return sentenceByKey;
}

/**
 * The id of the line whose recording covers this line record's words.
 *
 * For an ordinary line that is the line itself. For a **span replacement** it is
 * the sentence the replacement was spliced into, because that is the recording
 * occupying the same seconds.
 *
 * **Why this cannot be the record's own id.** A span replacement is stored
 * twice: as an extra line record whose id starts `o_`, and as a fragment the
 * backend splices into its host sentence, recording ONE line for that sentence
 * under the host's `p_` anchor. No recording is ever filed under an `o_` id, so
 * asking the manifest for one reports the replacement as never dubbed, for ever,
 * while its audio is right there inside the host's recording — counted as a gap
 * of silence that does not exist, and as a line the story does not have.
 */
export function lineIdOf(
  id: string,
  firstKey: string | undefined,
  sentenceByKey: ReadonlyMap<string, string>,
): string {
  if (!isOverrideId(id)) return id;
  // Nothing to resolve it through: a translation written before source words
  // carried stable ids names none of them. Nothing can place such a line in time
  // either, so it is reported as unplaceable under its own id, which is the one
  // a reader would look for.
  if (firstKey === undefined) return id;
  // The backend's own fallback when a replacement's words belong to no base
  // fragment: it becomes a sentence of its own under a `p_` anchor, and that is
  // the id its recording carries.
  return sentenceByKey.get(firstKey) ?? `p_${firstKey}`;
}

/**
 * Rebuild each segment's translated text from the fragments that are live RIGHT
 * NOW, the way the backend builds the text it records alongside the audio.
 *
 * It follows the backend's rules rather than inventing its own, because any
 * drift here reports an edit nobody made or hides one somebody did.
 *
 * **Two deliberate departures, both forced by the plugin's own stored data.**
 *
 * 1. The backend reads the element's `ts`. Here every real element carries `ts`
 *    as `null` with `media_ts` holding the time. Same ordering authority,
 *    different field.
 * 2. **A fragment naming an element this transcript does not carry keeps its
 *    place in the stored order instead of falling to the end.** The backend never
 *    meets this: it built the recording against the transcript as it stood then,
 *    where every id resolved. A snapshot taken later does not carry them all, and
 *    sorting those to the end rewrites a sentence into the same words in a
 *    different order — an edit nobody made. So an unresolved piece inherits the
 *    time of the last resolved piece before it, and the sort is stable, which
 *    leaves it where the stored order puts it. That stored order is also why the
 *    backend's tiebreak on element id is not copied: two pieces at the same
 *    moment stay in the order the translation stores them.
 */
interface RebuiltSegmentText {
  /** The line's words as they read now, joined the way the backend joins them. */
  text: string;
  /**
   * PLAT-11905 — the live fragments that text is made of, in the order it joins
   * them, with each one's `[start, end)` in `text`. The one owner of that order:
   * a dub-timed caption word finds its fragment here.
   */
  fragments: { phrase: TranslationPhrase; start: number; end: number }[];
  /**
   * Whether the ORDER of those words could be trusted.
   *
   * `false` when some of the line's fragments name transcript elements this
   * transcript no longer contains. The words are all still there; where they sit
   * relative to each other is a guess, because the timeline that would answer it
   * is gone. A difference in order alone is then not an edit.
   */
  orderIsKnown: boolean;
}

function rebuildSegmentTexts(
  fragments: TranslationPhrase[],
  byKey: Map<string, CoverageElement[]>,
  sentenceByKey: Map<string, string>,
  places: TranscriptPlaces,
): Map<string, RebuiltSegmentText> {
  const isRetired = (p: TranslationPhrase): boolean => p.active === false;

  /**
   * Where a piece sits on the timeline, and — when nothing can time it — WHY.
   * The two reasons must not be merged:
   *
   * - `'untimed'` — its elements are there and simply carry no time, which is
   *   punctuation. The backend puts these at the end of the sentence — except a
   *   punctuation-only fragment next to a word, which `pieceOf` places beside
   *   that word, as the backend does.
   * - `'absent'` — the transcript does not contain those elements at all. The
   *   recording was made when it did, so the backend HAD a time for them, and
   *   sending these to the end rewrites the sentence.
   */
  const startOf = (keys: string[]): { sec: number } | 'untimed' | 'absent' => {
    let earliest: number | null = null;
    let found = false;
    for (const key of keys) {
      for (const element of byKey.get(String(key)) ?? []) {
        found = true;
        if (element.type !== 'text') continue;
        const start = element.media_ts;
        if (start === undefined || start === null) continue;
        if (earliest === null || start < earliest) earliest = start;
      }
    }
    if (earliest !== null) return { sec: earliest };
    return found ? 'untimed' : 'absent';
  };

  const overrides = fragments.filter(
    p => isOverrideId(p.parentId) && !isRetired(p),
  );
  const overriddenIds = new Set<string>();
  for (const override of overrides)
    for (const key of keysOf(override)) overriddenIds.add(key);

  interface Piece {
    /** A time, or why there isn't one. Resolved to a number below. */
    at: { sec: number } | 'untimed' | 'absent';
    text: string;
    keys: string[];
    phrase: TranslationPhrase;
    /**
     * Transcript positions that break a tie in time: the word the piece sits
     * beside, then the piece's own. `Infinity` when it has none.
     */
    anchorIndex: number;
    ownIndex: number;
    /** Untimed punctuation placed beside the word next to it. */
    placed: boolean;
  }
  const bySegment = new Map<string, Piece[]>();
  const push = (segmentId: string, piece: Piece): void => {
    const existing = bySegment.get(segmentId);
    if (existing) existing.push(piece);
    else bySegment.set(segmentId, [piece]);
  };
  const pieceOf = (p: TranslationPhrase): Piece => {
    const keys = keysOf(p);
    const at = startOf(keys);
    const text = (p.translatedValue ?? '').trim();
    const elements = keys.flatMap(key => byKey.get(String(key)) ?? []);
    const indices = elements.flatMap(element => {
      const index = places.indexOf.get(element);
      return index === undefined ? [] : [index];
    });
    const ownIndex = indices.length > 0 ? Math.min(...indices) : Infinity;
    if (at === 'untimed' && isPunctuationOnly(text)) {
      // An opener sits before the word after it; anything else after the word
      // before it. Either falls back to the other side at a transcript edge.
      const opens = joinsNextWord(text);
      const anchors = elements.flatMap(element => {
        const side = places.neighbours.get(element);
        const anchor = opens
          ? (side?.after ?? side?.before)
          : (side?.before ?? side?.after);
        return anchor ? [anchor] : [];
      });
      if (anchors.length > 0) {
        const anchor = anchors.reduce((a, b) => (b.index < a.index ? b : a));
        return {
          at: { sec: anchor.sec },
          text,
          keys,
          phrase: p,
          anchorIndex: anchor.index,
          ownIndex,
          placed: true,
        };
      }
    }
    // A piece with words sits where its first timed word does, as the backend
    // places it — not at punctuation it also covers ahead of that word.
    const wordIndices = elements.flatMap(element => {
      const index = places.indexOf.get(element);
      const start = element.type === 'text' ? element.media_ts : null;
      return index === undefined || start === undefined || start === null
        ? []
        : [index];
    });
    const wordIndex =
      wordIndices.length > 0 ? Math.min(...wordIndices) : Infinity;
    return {
      at,
      text,
      keys,
      phrase: p,
      anchorIndex: wordIndex,
      ownIndex: wordIndex,
      placed: false,
    };
  };

  for (const fragment of fragments) {
    if (isOverrideId(fragment.parentId) || isRetired(fragment)) continue;
    const keys = keysOf(fragment);
    if (keys.length > 0 && keys.every(key => overriddenIds.has(key))) continue;
    push(anchorOf(fragment), pieceOf(fragment));
  }
  for (const override of overrides) {
    const host = sentenceByKey.get(keysOf(override)[0]) ?? anchorOf(override);
    push(host, pieceOf(override));
  }

  const texts = new Map<string, RebuiltSegmentText>();
  for (const [segmentId, pieces] of bySegment) {
    // Punctuation goes to the end, as the backend puts it. A piece whose
    // elements are missing from this transcript keeps the place the stored order
    // gives it — it inherits the time of the last piece before it that could be
    // placed, and the sort is stable, so it lands where a reader sees it.
    let carried = -Infinity;
    const placed = pieces.map(piece => {
      if (piece.at !== 'untimed' && piece.at !== 'absent')
        carried = piece.at.sec;
      const startSec =
        piece.at === 'untimed'
          ? Infinity
          : piece.at === 'absent'
            ? carried
            : piece.at.sec;
      return { ...piece, startSec };
    });
    const ordered = placed
      .filter(piece => piece.text.length > 0)
      // A tie in time falls through to transcript position, then to the id.
      // Two untimed pieces both sit at `Infinity` and tie all the way to the
      // id — the backend's ordering rule for them.
      .sort(
        (a, b) =>
          compareAscending(a.startSec, b.startSec) ||
          compareAscending(a.anchorIndex, b.anchorIndex) ||
          compareAscending(a.ownIndex, b.ownIndex) ||
          (a.keys[0] ?? '').localeCompare(b.keys[0] ?? ''),
      );
    // Placed punctuation attaches with no space: an opener to the word after
    // it, a dash to neither, anything else to the word before it.
    let text = '';
    const ranges = ordered.map((piece, i) => {
      const previous = ordered[i - 1];
      const attachesBack =
        piece.placed && !joinsNextWord(piece.text) && !standsAlone(piece.text);
      const previousOpens = previous?.placed && joinsNextWord(previous.text);
      if (i > 0 && !attachesBack && !previousOpens) text += ' ';
      const start = text.length;
      text += piece.text;
      return { phrase: piece.phrase, start, end: text.length };
    });
    texts.set(segmentId, {
      orderIsKnown: pieces.every(piece => piece.at !== 'absent'),
      text,
      fragments: ranges,
    });
  }
  return texts;
}

/**
 * Do the words the audio was voiced from differ from the words the line carries
 * now?
 *
 * The shared normaliser collapses runs of whitespace and drops a space before
 * punctuation — the only ways the dub's token join and the translation's display
 * string differ on a line nobody has touched. Everything else counts, including
 * a change that is punctuation alone, which does change how a line is read
 * aloud.
 *
 * **When the order could not be reconstructed** the comparison drops to the
 * words alone, ignoring their order, because telling someone their dub is out of
 * date when it is not is the failure this exists to prevent. The accepted cost
 * is that on those lines an edit that ONLY reorders words is not caught; every
 * edit that changes a word still is.
 */
function changed(
  dubbedFrom: string,
  translationNow: string,
  orderIsKnown: boolean,
): boolean {
  const a = normalizeForCompare(dubbedFrom);
  const b = normalizeForCompare(translationNow);
  if (orderIsKnown) return a !== b;
  const bag = (text: string) =>
    text.split(/\s+/).filter(Boolean).sort().join(' ');
  return bag(a) !== bag(b);
}

export function computeDubCoverage(
  input: DubCoverageInput,
): Result<DubCoverage> {
  const { spans, phrases, fragments, elements, manifest } = input;

  if (!Array.isArray(spans) || spans.length === 0) {
    return err('VALIDATION', 'computeDubCoverage: the clip has no kept spans');
  }
  if (!Array.isArray(phrases)) {
    return err(
      'VALIDATION',
      'computeDubCoverage: no translation phrases were supplied',
    );
  }
  // Refused rather than defaulted to an empty list: with no fragments every
  // rebuilt line comes back empty and every dubbed line is reported out of date
  // — a wrong answer that looks like a real one.
  if (!Array.isArray(fragments)) {
    return err(
      'VALIDATION',
      'computeDubCoverage: no translation fragments were supplied',
    );
  }

  // A manifest with no live segments is a valid "nothing is dubbed" answer, not
  // an error — it is the state every clip starts in.
  const live = manifest ? dropSuperseded(manifest.segments ?? []).live : [];
  const dubbedById = new Map(live.map(segment => [segment.segmentId, segment]));

  const byKey = indexElements(elements ?? []);
  const sentenceByKey = indexSentenceByElement(fragments);
  const textNowBySegment = rebuildSegmentTexts(
    fragments,
    byKey,
    sentenceByKey,
    indexTranscriptPlaces(elements ?? []),
  );
  // The line records the story really has, so a span replacement can be seen to
  // be already counted under the sentence it was spliced into.
  const lineRecordIds = new Set(phrases.map(phrase => phrase.id));

  const gaps: DubCoverageGap[] = [];
  const stale: DubCoverageStale[] = [];
  const unlocatable: string[] = [];
  const lines: Array<{ lineId: string; startSec: number }> = [];
  let inClip = 0;
  let dubbed = 0;
  let gapSeconds = 0;
  let staleSeconds = 0;

  for (const phrase of phrases) {
    const lineId = lineIdOf(phrase.id, keysOf(phrase)[0], sentenceByKey);
    // Already counted, under that sentence's own record. Counting it again
    // counts the same seconds twice — once as covered inside the host, once as
    // silence that is not there.
    if (lineId !== phrase.id && lineRecordIds.has(lineId)) continue;

    const time = timeOfPhrase(phrase, byKey);
    if (!time) {
      unlocatable.push(lineId);
      continue;
    }
    if (!overlapsAnySpan(time.startSec, time.endSec, spans)) continue;

    inClip += 1;
    lines.push({ lineId, startSec: time.startSec });
    const segment = dubbedById.get(lineId);
    if (!segment) {
      const keptSec = keptDurationSec(time.startSec, time.endSec, spans);
      gapSeconds += keptSec;
      gaps.push({
        phraseId: lineId,
        startSec: time.startSec,
        endSec: time.endSec,
        keptSec,
        text: phrase.translatedValue,
      });
      continue;
    }

    dubbed += 1;
    // The words this line carries NOW, rebuilt from its live fragments. A line
    // whose fragments have all been deleted rebuilds to nothing, which differs
    // from whatever was recorded — and a dub of words that no longer exist IS
    // out of date, so that is the right answer rather than an edge case.
    const rebuilt = textNowBySegment.get(lineId);
    const translationNow = rebuilt?.text ?? '';
    if (
      changed(
        segment.sourceTranslationSnapshot,
        translationNow,
        rebuilt?.orderIsKnown ?? true,
      )
    ) {
      staleSeconds += keptDurationSec(time.startSec, time.endSec, spans);
      stale.push({
        phraseId: lineId,
        startSec: time.startSec,
        endSec: time.endSec,
        dubbedFrom: segment.sourceTranslationSnapshot,
        translationNow,
      });
    }
  }

  const byStart = <T extends { startSec: number }>(a: T, b: T) =>
    a.startSec - b.startSec;

  return ok({
    inClip,
    lineIds: [...new Set(lines.sort(byStart).map(line => line.lineId))],
    dubbed,
    gaps: gaps.sort(byStart),
    stale: stale.sort(byStart),
    gapSeconds,
    staleSeconds,
    unlocatable,
  });
}

// ─── PLAT-11678 — shared with the automatic re-dub plan ─────────────────────
//
// The auto re-dub (`services/dubbing/autoRedubPlan.ts`) has to answer the same
// question the out-of-date warning answers — "does this line's recording still
// match its words?" — and it has to answer it identically, or a reviewer sees a
// warning the auto re-dub did not act on, or the reverse. So the two read the
// text and compare it through these two exports rather than each keeping a copy
// of the rules above.

/**
 * A fragment's dub line: its `parentId`, except for a span replacement (`o_`),
 * whose line is the sentence it was spliced into, resolved by `lineIdOf`: no
 * recording is filed under an `o_` id, so sending one re-dubs nothing and
 * looking words up by one finds none. A fragment with no `parentId` has no line.
 *
 * `allFragments` is the language's whole phrase list, superseded included, so a
 * replacement finds its host through the base fragments it displaced. The
 * sentence index is built on the first replacement asked about, once.
 */
export function dubLineResolver(
  allFragments: TranslationPhrase[],
): (fragment: TranslationPhrase) => string | undefined {
  let sentenceByKey: Map<string, string> | undefined;
  return fragment => {
    const parentId = fragment.parentId;
    if (!parentId || !isOverrideId(parentId)) return parentId;
    sentenceByKey ??= indexSentenceByElement(allFragments);
    return lineIdOf(parentId, keysOf(fragment)[0], sentenceByKey);
  };
}

/**
 * The distinct dub lines a set of fragments falls in (`dubLineResolver`), in
 * fragment order — the `segmentIds` a re-dub of a selection sends.
 */
export function dubLineIdsForFragments(
  fragments: readonly TranslationPhrase[],
  allFragments: TranslationPhrase[],
): string[] {
  const lineOf = dubLineResolver(allFragments);
  const ids = new Set<string>();
  for (const fragment of fragments) {
    const lineId = lineOf(fragment);
    if (lineId) ids.add(lineId);
  }
  return [...ids];
}

/**
 * Each line's translated text as it reads now, keyed by the id its recording is
 * filed under (`lineIdOf`), plus whether the word order could be trusted.
 */
export function rebuildSegmentTextsNow(
  fragments: TranslationPhrase[],
  elements: TranscriptElement[],
): {
  textByLineId: Map<string, string>;
  orderKnownByLineId: Map<string, boolean>;
  /** Source-element id → the `p_` anchor of the sentence that owns it. */
  sentenceByKey: Map<string, string>;
} {
  const byKey = indexElements(elements ?? []);
  const sentenceByKey = indexSentenceByElement(fragments);
  const rebuilt = rebuildSegmentTexts(
    fragments,
    byKey,
    sentenceByKey,
    indexTranscriptPlaces(elements ?? []),
  );
  const textByLineId = new Map<string, string>();
  const orderKnownByLineId = new Map<string, boolean>();
  for (const [lineId, line] of rebuilt) {
    textByLineId.set(lineId, line.text);
    orderKnownByLineId.set(lineId, line.orderIsKnown);
  }
  return { textByLineId, orderKnownByLineId, sentenceByKey };
}

/**
 * PLAT-11905 — each line's text as it reads now and the live fragments it is
 * made of, each with its `[start, end)` in that text, in the order the text
 * joins them. Keyed like `rebuildSegmentTextsNow`, and built by the same rules,
 * so the order has one owner. Lets a dub-timed caption word find the fragment
 * it belongs to.
 */
export function lineFragmentRanges(
  fragments: TranslationPhrase[],
  elements: readonly CoverageElement[],
): Map<
  string,
  {
    text: string;
    orderIsKnown: boolean;
    fragments: { phrase: TranslationPhrase; start: number; end: number }[];
  }
> {
  return rebuildSegmentTexts(
    fragments,
    indexElements(elements ?? []),
    indexSentenceByElement(fragments),
    indexTranscriptPlaces(elements ?? []),
  );
}

/**
 * PLAT-11906 — can a dubbed line's words be trusted to sit where the
 * translation's fragments say? Only when the manifest stored its words, its
 * rebuilt text matches the text it was recorded from, it is not out of date
 * (the same test the dub's out-of-date warning uses), and the fragments,
 * joined as `lineFragmentRanges` joins them, read exactly as the line does —
 * so a word's character range is also its range in the fragments' text.
 * Every reader that puts a dub word on a fragment asks this, so the caption
 * words and the review's word-by-word reading agree on which lines qualify.
 */
export function dubLineMatchesFragments(
  line: { spokenText: string; wordsKnown: boolean; snapshotMatches: boolean },
  now: { text: string; orderIsKnown: boolean },
): boolean {
  if (!line.wordsKnown || !line.snapshotMatches) return false;
  if (changed(line.spokenText, now.text, now.orderIsKnown)) return false;
  return now.text === line.spokenText;
}

/**
 * Whether the words a line was voiced from differ from the words it carries
 * now, by the rules `changed` documents above.
 */
export function dubTextChanged(
  dubbedFrom: string,
  translationNow: string,
  orderIsKnown = true,
): boolean {
  return changed(dubbedFrom, translationNow, orderIsKnown);
}
